// Matching names read by Claude to buildings, companies and people already in PM C3 (case-insensitive, other names count).
export type NamedThing = { id: string; name: string; short_code?: string | null; aliases?: string[] | null };
export type Contact = { id: string; org_id: string | null; full_name: string | null };

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** Best match for a name: exact name / code / other name first, then "contains" either way (longest key wins). */
export function matchName<T extends NamedThing>(raw: string | null | undefined, list: T[]): T | null {
  if (!raw) return null;
  const n = norm(raw);
  if (!n) return null;
  let best: { t: T; score: number } | null = null;
  for (const t of list) {
    const keys = [t.name, t.short_code ?? '', ...(t.aliases ?? [])].map(norm).filter((k) => k.length >= 2);
    for (const k of keys) {
      let score = 0;
      if (k === n) score = 1000 + k.length;
      else if (k.length >= 3 && (` ${n} `.includes(` ${k} `) || (n.length >= 4 && ` ${k} `.includes(` ${n} `)))) score = k.length;
      if (score && (!best || score > best.score)) best = { t, score };
    }
  }
  return best?.t ?? null;
}

/** "Chris" → the contact whose first name is Chris (only when exactly one company has such a person). */
export function matchPerson(first: string | null | undefined, contacts: Contact[]): Contact | null {
  if (!first) return null;
  const f = norm(first).split(' ')[0];
  if (!f || f.length < 2) return null;
  const hits = contacts.filter((c) => c.org_id && norm(c.full_name ?? '').split(' ')[0] === f);
  const orgs = new Set(hits.map((h) => h.org_id));
  return orgs.size === 1 ? hits[0] : null;
}

export const addDays = (iso: string, d: number) => {
  const t = new Date(iso + 'T00:00:00Z');
  t.setUTCDate(t.getUTCDate() + d);
  return t.toISOString().slice(0, 10);
};

/** Contractor refs compared loosely: "PBS RFI #27" = "pbs rfi 27". */
export const sameRef = (a: string | null | undefined, b: string | null | undefined) => {
  const x = norm(a ?? '').replace(/\s+/g, '');
  const y = norm(b ?? '').replace(/\s+/g, '');
  return Boolean(x && y && x === y);
};

/** Words worth comparing for "related items" suggestions. */
export const words = (s: string) => new Set(norm(s).split(' ').filter((w) => w.length > 4));
